import { useEffect, useMemo, useState } from 'react'
import { ApiError, api } from '@/api/client'
import { LoadingScreen } from '@/components/LoadingScreen'
import { Select } from '@/components/Select'
import { useTranslation } from '@/i18n'

type CatalogSeason = {
  id: number
  name: string
  start_year: number
  end_year: number
}

type CatalogTeam = {
  id: number
  name: string
}

type CatalogResponse = {
  seasons: CatalogSeason[]
  teams: CatalogTeam[]
}

type PlayerCard = {
  id: number
  player: string
  birth_date: string | null
  team: string | null
  season: string | null
  position: string | null
  rating: number
  price: number | null
  projected_points: number | null
  rating_confidence: string | null
  source: string | null
  source_url: string | null
}

type PlayerCardsResponse = {
  data: PlayerCard[]
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message.trim()) return error.message
  return fallback
}

function ratingValue(rating: number) {
  return Number.isInteger(rating) ? String(rating) : rating.toFixed(2)
}

export function AdminSquadBuilderPage() {
  const { t } = useTranslation()
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [seasonId, setSeasonId] = useState<number | null>(null)
  const [teamId, setTeamId] = useState<number | null>(null)
  const [cards, setCards] = useState<PlayerCard[]>([])
  const [draftRatings, setDraftRatings] = useState<Record<number, string>>({})
  const [cardsLoading, setCardsLoading] = useState(false)
  const [cardsError, setCardsError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setCatalogLoading(true)
    setCatalogError(null)

    void api
      .get<CatalogResponse>('/admin/squad-builder/catalog', { signal: controller.signal })
      .then((response) => {
        setCatalog(response)
        setSeasonId((current) => current ?? response.seasons[0]?.id ?? null)
        setTeamId((current) => current ?? response.teams[0]?.id ?? null)
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setCatalogError(getErrorMessage(error, 'De seizoenen en clubs konden niet worden geladen.'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setCatalogLoading(false)
      })

    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (seasonId === null || teamId === null) {
      setCards([])
      setDraftRatings({})
      return
    }

    const controller = new AbortController()
    const query = new URLSearchParams({
      season_id: String(seasonId),
      team_id: String(teamId),
    })

    setCardsLoading(true)
    setCardsError(null)
    setSaveError(null)
    setSavedMessage(null)

    void api
      .get<PlayerCardsResponse>(`/admin/squad-builder/player-cards?${query.toString()}`, {
        signal: controller.signal,
      })
      .then((response) => {
        setCards(response.data)
        setDraftRatings(
          Object.fromEntries(response.data.map((card) => [card.id, ratingValue(card.rating)])),
        )
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setCards([])
        setDraftRatings({})
        setCardsError(getErrorMessage(error, 'De spelers konden niet worden geladen.'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setCardsLoading(false)
      })

    return () => controller.abort()
  }, [seasonId, teamId])

  const dirtyCards = useMemo(
    () => cards.filter((card) => draftRatings[card.id] !== ratingValue(card.rating)),
    [cards, draftRatings],
  )

  const selectedSeason = catalog?.seasons.find((season) => season.id === seasonId)
  const selectedTeam = catalog?.teams.find((team) => team.id === teamId)

  function updateDraft(cardId: number, value: string) {
    setSavedMessage(null)
    setSaveError(null)
    setDraftRatings((current) => ({ ...current, [cardId]: value }))
  }

  async function saveRatings() {
    if (dirtyCards.length === 0 || saving) return

    setSaving(true)
    setSaveError(null)
    setSavedMessage(null)

    try {
      for (const card of dirtyCards) {
        const rawRating = draftRatings[card.id]?.trim() ?? ''
        const rating = Number(rawRating)

        if (!rawRating || !Number.isFinite(rating) || rating < 0 || rating > 100) {
          throw new Error(t('De rating van {player} moet tussen 0 en 100 liggen.', { player: card.player }))
        }

        const response = await api.patch<{ data: { id: number; rating: number } }>(
          `/admin/squad-builder/player-cards/${card.id}/rating`,
          { rating },
        )

        const persistedRating = response.data.rating
        setCards((current) => current.map((item) =>
          item.id === card.id ? { ...item, rating: persistedRating } : item,
        ))
        setDraftRatings((current) => ({ ...current, [card.id]: ratingValue(persistedRating) }))
      }

      setSavedMessage(t('{count} ratings opgeslagen.', { count: dirtyCards.length }))
    } catch (error: unknown) {
      setSaveError(getErrorMessage(error, t('De ratings konden niet worden opgeslagen.')))
    } finally {
      setSaving(false)
    }
  }

  if (catalogLoading) {
    return (
      <div className="space-y-5">
        <LoadingScreen compact />
        <AdminSquadBuilderSkeleton />
      </div>
    )
  }

  if (catalogError || !catalog) {
    return (
      <section className="rounded-[1.5rem] border border-red-300/15 bg-[#0d1113] p-6 sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-red-300">{t('Squad Builder beheer')}</p>
        <h1 className="mt-3 text-2xl font-black tracking-tight text-white">{t('Beheer niet geladen')}</h1>
        <p className="mt-2 text-sm leading-6 text-surface-400">{catalogError ?? t('Geen beheerdata beschikbaar.')}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
        >
          {t('Opnieuw proberen')}
        </button>
      </section>
    )
  }

  return (
    <div className="space-y-5 pb-10">
      <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Admin · Squad Builder')}</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-5">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">{t('Seizoensratings beheren')}</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">
              {t('Kies een seizoen en club en pas de ratings van de spelers direct aan.')}
            </p>
          </div>
          <div className="rounded-full border border-emerald-300/15 bg-emerald-300/10 px-3 py-1.5 text-xs font-semibold text-emerald-200">
            {t('Alleen admins')}
          </div>
        </div>
      </section>

      <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-surface-500">{t('Seizoen')}</span>
            <span className="mt-2 block"><Select value={seasonId == null ? '' : String(seasonId)} onChange={(value) => setSeasonId(Number(value) || null)} options={catalog.seasons.map((season) => ({ value: String(season.id), label: season.name }))} placeholder={t('Kies seizoen')} ariaLabel={t('Seizoen')} /></span>
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-surface-500">{t('Club')}</span>
            <span className="mt-2 block"><Select value={teamId == null ? '' : String(teamId)} onChange={(value) => setTeamId(Number(value) || null)} options={catalog.teams.map((team) => ({ value: String(team.id), label: team.name }))} placeholder={t('Kies club')} ariaLabel={t('Club')} /></span>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.07] pt-4">
          <p className="text-sm text-surface-400">
            {selectedTeam?.name ?? t('Club')} · {selectedSeason?.name ?? t('Seizoen')} · {cards.length} {t('spelers')}
          </p>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="text-xs text-surface-500">
              {t('Wijzigingen worden automatisch opgeslagen.')}
            </span>
            {dirtyCards.length > 0 ? (
              <button
                type="button"
                onClick={() => void saveRatings()}
                disabled={saving || cardsLoading}
                className="rounded-xl border border-white/[0.12] bg-[#151a1c] px-3.5 py-2.5 text-sm font-bold text-white transition hover:bg-white/[0.09] disabled:cursor-not-allowed disabled:opacity-45"
              >
                {saving ? t('Opslaan…') : t('Nu opslaan')}
              </button>
            ) : null}
          </div>
        </div>

        {savedMessage ? <p className="mt-3 text-sm font-semibold text-emerald-300">{savedMessage}</p> : null}
        {saveError ? <p className="mt-3 text-sm font-semibold text-red-300">{saveError}</p> : null}
      </section>

      <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-surface-500">{t('Spelers')}</p>
            <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Ratings')}</h2>
          </div>
          {cardsLoading ? <span className="text-xs font-semibold text-surface-500">{t('Laden…')}</span> : null}
        </div>

        {cardsError ? <p className="mt-5 text-sm text-red-300">{cardsError}</p> : null}
        {!cardsLoading && !cardsError && cards.length === 0 ? (
          <p className="mt-5 rounded-xl border border-dashed border-white/[0.1] px-4 py-5 text-sm text-surface-500">
            {t('Voor deze club en dit seizoen zijn geen spelerskaarten gevonden.')}
          </p>
        ) : null}

        {cards.length > 0 ? (
          <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07]">
            <div className="hidden grid-cols-[minmax(0,1fr)_7rem_8rem] gap-4 border-b border-white/[0.07] bg-[#111719] px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-surface-500 sm:grid">
              <span>{t('Speler')}</span>
              <span>{t('Positie')}</span>
              <span>{t('Rating')}</span>
            </div>
            <div className="divide-y divide-white/[0.07]">
              {cards.map((card) => {
                const draft = draftRatings[card.id] ?? ''
                const changed = draft !== ratingValue(card.rating)

                return (
                  <div key={card.id} className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_7rem_8rem] sm:items-center sm:gap-4">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white">{card.player}</p>
                      <p className="mt-1 text-xs text-surface-500">{card.team ?? selectedTeam?.name ?? t('Onbekende club')}{card.birth_date ? ` · ${card.birth_date}` : ''}</p>
                    </div>
                    <div className="text-sm font-semibold text-surface-400">{card.position ?? '—'}</div>
                    <label className="flex items-center gap-2 sm:block">
                      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-surface-500 sm:hidden">{t('Rating')}</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={draft}
                        onChange={(event) => updateDraft(card.id, event.target.value)}
                        onBlur={() => void saveRatings()}
                        className={[
                          'w-full rounded-lg border bg-[#111719] px-3 py-2 text-sm font-bold text-white outline-none transition focus:border-accent-400/70',
                          changed ? 'border-accent-400/60' : 'border-white/[0.1]',
                        ].join(' ')}
                        aria-label={t('Rating van {player}', { player: card.player })}
                      />
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  )
}

function AdminSquadBuilderSkeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      <div className="h-40 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      <div className="h-36 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      <div className="h-[28rem] animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
    </div>
  )
}
